import { apiClient, ApiError } from './client'
import type {
  UserProfileData,
  MeResponse,
  BranchListResponse,
  ExchangeRateListRequest,
  ExchangeRateListResponse,
  ExchangeRateGetRequest,
  ExchangeRateGetResponse,
  ExchangeCreateRequest,
  ExchangeCreateResponse,
  LoginRequest,
  LoginResponseData,
  UploadStartRequest,
  UploadStartResponse,
  ExchangeListParams,
  ExchangeListResponse,
  ExchangeDeleteRequest,
  ExchangeDeleteResponse,
  SettlementStatusResponse,
  SettlementCloseRequest,
  SettlementCloseResponse,
  TransferPlanCreateRequest,
  TransferPlanCreateResponse,
  TransferPlanListRequest,
  TransferPlanListResponse,
  TransferPlanResolveRequest,
  TransferPlanResolveResponse,
  TransferPlanUpdateRequest,
  TransferPlanUpdateResponse,
} from './types'

// ============================================
// Auth Services
// ============================================

/**
 * Login user with email and password
 * New API: Two-step process
 *   1. POST /auth/token -> get JWT token
 *   2. GET /me -> get user profile and branches
 *
 * @throws ApiError if login or profile fetch fails
 * @returns Object containing user data, branches, and JWT token
 */
export async function login(email: string, password: string): Promise<{
  data: LoginResponseData
  token: string
}> {
  const loginRequest: LoginRequest = { email, password }

  try {
    // STEP 1: Get authentication token
    console.log('🔐 Step 1: Authenticating...')
    const tokenResponse = await fetch(`${apiClient.baseUrl}/auth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(loginRequest),
    })

    // Handle login errors
    if (!tokenResponse.ok) {
      let errorData
      try {
        errorData = await tokenResponse.json()
      } catch {
        throw new ApiError(
          'LOGIN_FAILED',
          `Login failed with status ${tokenResponse.status}`,
          tokenResponse.status
        )
      }

      throw new ApiError(
        errorData.error?.code || 'LOGIN_FAILED',
        errorData.error?.message || 'Invalid credentials',
        tokenResponse.status
      )
    }

    // Extract token from JSON response
    const tokenData = await tokenResponse.json()

    // Validate token response structure
    if (!tokenData.data || !tokenData.data.token) {
      throw new ApiError(
        'INVALID_RESPONSE',
        'Invalid login response: missing token',
        500
      )
    }

    const token = tokenData.data.token
    console.log('✓ Step 1: Token received', token)

    // STEP 2: Fetch user profile and branches
    console.log('👤 Step 2: Fetching user profile...')
    const profileResponse = await fetch(`${apiClient.baseUrl}/me`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({}),
    })

    // Handle profile fetch errors
    if (!profileResponse.ok) {
      let errorData
      try {
        errorData = await profileResponse.json()
      } catch {
        throw new ApiError(
          'PROFILE_FETCH_FAILED',
          `Failed to fetch user profile (status ${profileResponse.status})`,
          profileResponse.status
        )
      }

      throw new ApiError(
        errorData.error?.code || 'PROFILE_FETCH_FAILED',
        errorData.error?.message || 'Failed to fetch user profile',
        profileResponse.status
      )
    }

    // Parse profile data
    const profileData = await profileResponse.json()

    // Validate profile response structure
    if (!profileData.data || !profileData.data.user || !profileData.data.branches) {
      throw new ApiError(
        'INVALID_RESPONSE',
        'Invalid profile response: missing user or branches',
        500
      )
    }

    console.log('✓ Step 2: Profile fetched')
    console.log('✓ Login successful')

    // Return combined data in expected format
    return {
      data: {
        user: profileData.data.user,
        branches: profileData.data.branches,
      },
      token,
    }
  } catch (error) {
    // Re-throw ApiError as-is
    if (error instanceof ApiError) {
      throw error
    }

    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new ApiError(
        'NETWORK_ERROR',
        'Network connection failed. Please check your internet connection.',
        0
      )
    }

    // Unknown errors
    throw new ApiError(
      'UNKNOWN_ERROR',
      error instanceof Error ? error.message : 'Unknown error occurred',
      0
    )
  }
}

// ============================================
// User Profile Service
// ============================================
export async function getUserProfile(): Promise<UserProfileData> {
  const response = await apiClient.post<MeResponse>('/me', {})
  return response.user
}

// ============================================
// Branch Services
// ============================================
export async function getBranches(): Promise<BranchListResponse> {
  return apiClient.post<BranchListResponse>('/branches/list', {})
}

// ============================================
// Exchange Rate Services
// ============================================
export async function getExchangeRates(
  branchNid: string
): Promise<ExchangeRateListResponse> {
  console.log('getExchangeRates', branchNid)
  const request: ExchangeRateListRequest = { branchNid }
  return apiClient.post<ExchangeRateListResponse>('/exchange-rates/list', request)
}

export async function getExchangeRate(
  branchNid: string,
  currencyCode: string
): Promise<ExchangeRateGetResponse> {
  const request: ExchangeRateGetRequest = { branchNid, currencyCode }
  return apiClient.post<ExchangeRateGetResponse>('/exchange-rates/get', request)
}

// ============================================
// Exchange Transaction Services
// ============================================
export async function createExchange(
  request: ExchangeCreateRequest
): Promise<ExchangeCreateResponse> {
  return apiClient.post<ExchangeCreateResponse>('/exchanges/create', request)
}

export async function getExchanges(
  params: ExchangeListParams
): Promise<ExchangeListResponse> {
  return apiClient.post<ExchangeListResponse>('/exchanges/list', params)
}

export async function deleteExchange(
  request: ExchangeDeleteRequest
): Promise<ExchangeDeleteResponse> {
  return apiClient.post<ExchangeDeleteResponse>('/exchanges/delete', request)
}

// ============================================
// Upload Services
// ============================================

/**
 * Get presigned URL for uploading file to S3
 */
export async function startUpload(
  request: UploadStartRequest
): Promise<UploadStartResponse> {
  return apiClient.post<UploadStartResponse>('/uploads/start', request)
}

/**
 * Upload file directly to S3 using presigned URL
 */
export async function uploadToS3(
  presignedUrl: string,
  blob: Blob,
  contentType: string
): Promise<void> {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
    },
    body: blob,
  })

  if (!response.ok) {
    throw new ApiError(
      'UPLOAD_FAILED',
      `S3 upload failed with status ${response.status}`,
      response.status
    )
  }
}

/**
 * Convert base64 data URL to Blob
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const parts = dataUrl.split(',')
  const mime = parts[0].match(/:(.*?);/)?.[1] || 'image/jpeg'
  const bstr = atob(parts[1])
  const n = bstr.length
  const u8arr = new Uint8Array(n)
  for (let i = 0; i < n; i++) {
    u8arr[i] = bstr.charCodeAt(i)
  }
  return new Blob([u8arr], { type: mime })
}

// ============================================
// Settlement Services
// ============================================

/**
 * Get settlement status for all currencies in a branch
 */
export async function getSettlementStatus(
  branchNid: string
): Promise<SettlementStatusResponse> {
  return apiClient.post<SettlementStatusResponse>('/settlements/status', { branchNid })
}

/**
 * Close settlement for a specific currency
 */
export async function closeSettlement(
  request: SettlementCloseRequest
): Promise<SettlementCloseResponse> {
  return apiClient.post<SettlementCloseResponse>('/settlements/close', request)
}

// ============================================
// Transfer Plan Services
// ============================================

export async function createTransferPlan(
  request: TransferPlanCreateRequest
): Promise<TransferPlanCreateResponse> {
  return apiClient.post<TransferPlanCreateResponse>('/transfer-plans/create', request)
}

export async function listTransferPlans(
  request: TransferPlanListRequest
): Promise<TransferPlanListResponse> {
  return apiClient.post<TransferPlanListResponse>('/transfer-plans/list', request)
}

export async function resolveTransferPlan(
  request: TransferPlanResolveRequest
): Promise<TransferPlanResolveResponse> {
  return apiClient.post<TransferPlanResolveResponse>('/transfer-plans/resolve', request)
}

export async function updateTransferPlan(
  request: TransferPlanUpdateRequest
): Promise<TransferPlanUpdateResponse> {
  return apiClient.post<TransferPlanUpdateResponse>('/transfer-plans/update', request)
}
